import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Download, Upload, AlertTriangle, FileJson } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { downloadJson, fmtTs } from './_utils';

export default function BackupRestoreModal({ open, onOpenChange }) {
  const [restoring, setRestoring] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [confirmStep, setConfirmStep] = useState(0);
  const fileRef = useRef(null);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const res = await api.get('/routeops/backup');
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const name = `routeops-backup-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}.json`;
      downloadJson(res.data, name);
      toast.success('Backup downloaded');
    } catch (e) {
      toast.error('Backup failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setDownloading(false);
    }
  };

  const handleFilePick = (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        const counts = {
          bookings: (data.routeops_bookings || []).length,
          audit: (data.routeops_audit || []).length,
          routes: (data.routeops_routes || []).length,
          fleet: (data.routeops_fleet || []).length,
          notes: (data.routeops_notes || []).length,
          terminal: (data.routeops_terminal || []).length,
          settings: (data.routeops_settings || []).length,
        };
        setPreview({ counts, meta: data._meta, filename: f.name });
        setFileContent(data);
        setConfirmStep(0);
      } catch (e) {
        toast.error('Invalid JSON file');
      }
    };
    reader.readAsText(f);
  };

  const handleRestore = async () => {
    if (confirmStep < 1) {
      setConfirmStep(1);
      return;
    }
    try {
      setRestoring(true);
      const res = await api.post('/routeops/restore', fileContent);
      toast.success('Restore complete');
      setPreview(null);
      setFileContent(null);
      setConfirmStep(0);
      onOpenChange(false);
      // Force reload to reflect restored data
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error('Restore failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="backup-restore-modal">
        <DialogHeader>
          <DialogTitle>RouteOps Backup &amp; Restore</DialogTitle>
          <DialogDescription>
            Export or import a full JSON snapshot of every RouteOps record — bookings, routes, terminal state, notes, audit trail, and settings.
          </DialogDescription>
        </DialogHeader>

        {/* BACKUP */}
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
          <div className="flex items-center gap-2 mb-2 text-slate-900 font-semibold">
            <Download className="h-4 w-4" /> Download JSON Backup
          </div>
          <p className="text-xs text-slate-600 mb-3">
            The backup file contains API keys. Store it securely.
          </p>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            data-testid="backup-download-btn"
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Download className="h-4 w-4 mr-2" />
            {downloading ? 'Preparing…' : 'Download JSON'}
          </Button>
        </div>

        {/* RESTORE */}
        <div className="border border-red-200 rounded-lg p-4 bg-red-50">
          <div className="flex items-center gap-2 mb-2 text-red-900 font-semibold">
            <Upload className="h-4 w-4" /> Restore from File
          </div>
          <p className="text-xs text-red-700 mb-3 flex items-start gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            Restoring overwrites all current RouteOps data. This action is logged. Audit entries are merged, never replaced.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            onChange={handleFilePick}
            className="hidden"
          />
          {!preview ? (
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              data-testid="backup-pick-file-btn"
            >
              <FileJson className="h-4 w-4 mr-2" />
              Pick JSON file
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="text-xs bg-white border border-slate-200 rounded p-3 space-y-1">
                <div className="font-semibold text-slate-800">{preview.filename}</div>
                {preview.meta?.created_at && (
                  <div className="text-slate-500">Backup created: {fmtTs(preview.meta.created_at)}</div>
                )}
                <div className="grid grid-cols-2 gap-1 pt-2">
                  <div>Bookings: <span className="font-mono">{preview.counts.bookings}</span></div>
                  <div>Audit entries: <span className="font-mono">{preview.counts.audit}</span></div>
                  <div>Routes: <span className="font-mono">{preview.counts.routes}</span></div>
                  <div>Fleet: <span className="font-mono">{preview.counts.fleet}</span></div>
                  <div>Notes: <span className="font-mono">{preview.counts.notes}</span></div>
                  <div>Terminal states: <span className="font-mono">{preview.counts.terminal}</span></div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => { setPreview(null); setFileContent(null); setConfirmStep(0); }}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700"
                  onClick={handleRestore}
                  disabled={restoring}
                  data-testid="backup-confirm-restore-btn"
                >
                  {restoring ? 'Restoring…' : confirmStep === 0 ? 'Restore from File' : 'Click again to confirm'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
